import {ICheatPluginController} from '../../CheatModuleInterfaces';
import {CoreDI, DataStorage, lazyInject, storage} from '@gongxigames/framework/core';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {Xhr} from '@gongxigames/framework/slot/fragments/Xhr';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {DI} from '@gongxigames/framework/slot/configs/DI';

export type SessionData = {
    session_name: string;
    rounds_amount: number;
    game_name: string;
    session: number[];
    ID?: number;
};

type SessionResponse = {
    status?: boolean;
    sessions: SessionData[];
};

type SessionsControllerData = {
    sessions: SessionData[];
    currentSessionID?: number;
};

export class CheatSessionController implements ICheatPluginController {
    @lazyInject(CoreDI.GameConfig)
    protected readonly gameConfig!: GameConfig;

    @lazyInject(CoreDI.Settings)
    protected readonly settings!: SlotSettings;

    @lazyInject(DI.Logic)
    protected readonly logic!: ILogic;

    protected gameName!: string;

    protected sessions: SessionData[] = [];
    protected sessionsURL = '';

    public store!: DataStorage<SessionsControllerData>;

    public init(): void {
        this.store = new DataStorage<SessionsControllerData>({
            sessions: []
        });

        this.sessionsURL = this.settings.get('sessionsURL');

        this.gameName = this.gameConfig.gameName;

        Xhr.get(`${this.sessionsURL}?gameName=${this.gameName}`, this.onInitResponse.bind(this));
    }

    protected onInitResponse(response: SessionResponse): void {
        if (response.status) {
            this.addSessions(response.sessions);
        }
    }

    public set(sessionID: number): void {
        const {sessions} = this.store.data;
        const session = sessions.find(s => s.ID === sessionID);

        if (session) {
            storage.setItem('currentSession', JSON.stringify(session));
            this.store.set('currentSessionID', sessionID);
        }
    }

    public delete(id: number): void {
        Xhr.delete(this.sessionsURL, JSON.stringify({id}), (response: any) => {
            if (response.status) {
                const sessions = this.store.get('sessions').filter(session => session.ID !== id);

                storage.removeItem('currentSession');
                this.store.set('sessions', sessions);
                this.store.set('currentSessionID', -1);
            }
        });
    }

    public unselect(): void {
        storage.setItem('currentSession', '');
        this.store.set('currentSessionID', -1);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public save(session_name: string, rounds_amount: number): void {
        const data: SessionData = {
            session_name,
            rounds_amount,
            game_name: this.gameName,
            session: this.logic.getSession(rounds_amount)
        };
        const serialObj = JSON.stringify(data);

        Xhr.post(this.sessionsURL, serialObj, this.onAddedSession.bind(this));
    }

    protected getCurrentSession(): SessionData | undefined {
        const currentSessionString = storage.getItem('currentSession');

        if (currentSessionString) {
            return JSON.parse(currentSessionString) as SessionData;
        }
    }

    protected addSessions(sessions: SessionData[]): void {
        this.store.set('sessions', [...this.store.data.sessions, ...sessions]);

        const currentSession = this.getCurrentSession();

        if (currentSession) {
            if (sessions.find( el => el.ID === currentSession.ID)) {
                this.store.set('currentSessionID', currentSession.ID);
            } else {
                storage.removeItem('currentSession');
            }
        }
    }

    protected onAddedSession(response: any): void {
        if (response.status) {
            this.addSessions([response.session]);
        }
    }
}
